import { useMemo } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
  NavLink,
  Link,
} from 'react-router-dom';
import { SearchBar } from './components/molecules/SearchBar';
import type { SearchResult } from './api/products';
import { ToastProvider } from './contexts/ToastContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastContainer } from './components/atoms/ToastContainer';
import { ErrorBoundary } from './components/atoms/ErrorBoundary';
import { ProtectedRoute } from './components/ProtectedRoute';
import { UserDropdown } from './components/organisms/UserDropdown';
import { useFeatures } from './api/hooks';
import { RemoteRouteMount, remoteRouteManifest } from './microfrontends';
import { PlatformRuntimeProvider, usePlatformRuntime } from './runtime';
import { ActivatePage } from './pages/ActivatePage';
import { FeedbackPage } from './features/feedback/FeedbackPage';

const AUTH_ROUTE_PREFIX = '/auth/';

const appRoutePaths = remoteRouteManifest
  .map(({ routePath }) => routePath)
  .filter((routePath) => !routePath.startsWith(AUTH_ROUTE_PREFIX));
const authWallPublicRoutePaths = remoteRouteManifest
  .map(({ routePath }) => routePath)
  .filter((routePath) => routePath.startsWith(AUTH_ROUTE_PREFIX) || routePath === '/accept-invite');

export function Navigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const runtime = usePlatformRuntime();
  const { user, logout } = useAuth();
  const navigationAdapter = useMemo(
    () =>
      runtime.navigation.createAdapter((path, options) => navigate(path, options), {
        pathname: location.pathname,
        search: location.search,
      }),
    [runtime, navigate, location.pathname, location.search]
  );
  const searchAdapter = useMemo(
    () => runtime.search.createAdapter((path, options) => navigate(path, options)),
    [runtime, navigate]
  );
  const currentPath = navigationAdapter.currentPath();

  const handleSearchSubmit = (query: string) => {
    searchAdapter.submitGlobalSearch(query);
  };

  const handleSelectSearchResult = (result: SearchResult) => {
    searchAdapter.openSearchResult({ id: result.id });
  };

  return (
    <nav className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex">
            <div className="flex-shrink-0 flex items-center">
              <span className="text-lg font-bold text-gray-900">ABPL</span>
            </div>
            <div className="hidden sm:ml-8 sm:flex sm:space-x-4">
              <NavLink
                to="/products"
                className={({ isActive }) =>
                  `inline-flex items-center px-2 py-2 text-sm font-medium lg:px-3 ${
                    isActive && !currentPath.startsWith('/products/deactivated')
                      ? 'border-b-2 border-blue-500 text-gray-900'
                      : 'text-gray-500 hover:text-gray-700'
                  }`
                }
              >
                Products
              </NavLink>
              {user?.role === 'admin' ? (
                <NavLink
                  to="/products/deactivated"
                  className={() =>
                    `inline-flex items-center px-2 py-2 text-sm font-medium lg:px-3 ${
                      currentPath.startsWith('/products/deactivated')
                        ? 'border-b-2 border-blue-500 text-gray-900'
                        : 'text-gray-500 hover:text-gray-700'
                    }`
                  }
                >
                  Deactivated
                </NavLink>
              ) : null}
              <NavLink
                to="/upc-management"
                className={() =>
                  `inline-flex items-center px-2 py-2 text-sm font-medium lg:px-3 ${
                    currentPath.startsWith('/upc-management') ||
                    currentPath.startsWith('/submit-upc')
                      ? 'border-b-2 border-blue-500 text-gray-900'
                      : 'text-gray-500 hover:text-gray-700'
                  }`
                }
              >
                UPC Management
              </NavLink>
              <NavLink
                to="/settings"
                className={() =>
                  `inline-flex items-center px-2 py-2 text-sm font-medium lg:px-3 ${
                    currentPath.startsWith('/settings')
                      ? 'border-b-2 border-blue-500 text-gray-900'
                      : 'text-gray-500 hover:text-gray-700'
                  }`
                }
              >
                Settings
              </NavLink>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-64 lg:w-80 xl:w-96">
              <SearchBar
                placeholder="Search by UPC, name, or brand..."
                onSearch={handleSearchSubmit}
                onSelectResult={handleSelectSearchResult}
                enableAutocomplete={true}
                autocompleteLimit={10}
                debounceMs={500}
                fullWidth
              />
            </div>
            {user ? (
              <Link
                to="/feedback"
                className={`inline-flex items-center rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                  currentPath.startsWith('/feedback')
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Feedback
              </Link>
            ) : null}
            {user ? <UserDropdown user={user} onLogout={logout} /> : null}
          </div>
        </div>
      </div>
    </nav>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/products" replace />} />
      <Route path="/auth/activate" element={<ActivatePage />} />
      <Route
        path="/feedback"
        element={
          <ProtectedRoute>
            <FeedbackPage />
          </ProtectedRoute>
        }
      />
      {appRoutePaths.map((routePath) => (
        <Route
          key={routePath}
          path={routePath}
          element={<RemoteRouteMount routePath={routePath} />}
        />
      ))}
      <Route
        path="*"
        element={
          <div className="min-h-screen bg-gray-50 flex items-center justify-center">
            <div className="text-center">
              <h1 className="text-6xl font-bold text-gray-900 mb-4">404</h1>
              <p className="text-xl text-gray-600 mb-8">Page not found</p>
              <Link to="/products" className="text-blue-600 hover:text-blue-800 underline">
                Go to Products
              </Link>
            </div>
          </div>
        }
      />
    </Routes>
  );
}

function AuthWallLayout() {
  return (
    <Routes>
      {authWallPublicRoutePaths.map((routePath) => (
        <Route
          key={routePath}
          path={routePath}
          element={<RemoteRouteMount routePath={routePath} />}
        />
      ))}
      <Route path="/auth/activate" element={<ActivatePage />} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <Navigation />
            <AppRoutes />
            <ToastContainer />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function OpenLayout() {
  return (
    <>
      <Navigation />
      <AppRoutes />
      <ToastContainer />
    </>
  );
}

function App() {
  const { data: features, isLoading } = useFeatures();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  const authWallEnabled = features?.authWallEnabled ?? false;

  return (
    <PlatformRuntimeProvider>
      <ToastProvider>
        <AuthProvider>
          <ErrorBoundary>
            <BrowserRouter>{authWallEnabled ? <AuthWallLayout /> : <OpenLayout />}</BrowserRouter>
          </ErrorBoundary>
        </AuthProvider>
      </ToastProvider>
    </PlatformRuntimeProvider>
  );
}

export default App;
